/**
 * @file ProductsCompactList.tsx
 * @description Компактный список товаров до 1024 (phase19.31, макет (а) 360):
 * строка 72 — миниатюра 48, название во всю ширину первой линией, «категория ·
 * цена» второй, пилюля «На сайте» и остаток третьей; вся строка — ссылка, «⋯» 48 справа.
 *
 * Обёртка у зоны, а не общий `ResponsiveListCompact`: тому нечем показать
 * миниатюру слева, а товар без фото на телефоне не узнать (разбор B §1).
 * Рамка, поверхность и меню строки — общие (`LIST_FRAME_CLASS`, `RowActionsMenu`).
 */

import { Link } from "react-router";

import { AvailabilityCell } from "../AvailabilityCell";
import { ProductThumb } from "../ProductThumb";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import { StatusPill } from "../../ui/StatusPill";
import type { CatalogCategory, CatalogProduct } from "../../../lib/api-catalog";
import { categoryLabel } from "../../../lib/catalog-category-tree";
import { productPriceText } from "../../../lib/catalog-format";
import { PRODUCT_SITE_META, productSiteState } from "../../../lib/catalog-status";
import { cn } from "@/lib/utils";

interface Props {
  rows: CatalogProduct[];
  categoriesById: Map<string, CatalogCategory>;
  rowHref: (product: CatalogProduct) => string;
  rowActions: (product: CatalogProduct) => RowAction[];
}

export function ProductsCompactList({ rows, categoriesById, rowHref, rowActions }: Props) {
  return (
    <ul aria-label="Товары" className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
      {rows.map((p) => {
        const category = categoriesById.get(p.categoryId);
        return (
          <li key={p.id} className="flex items-stretch">
            <Link
              to={rowHref(p)}
              aria-label={`Товар ${p.displayName}`}
              className="flex min-h-18 min-w-0 flex-1 items-center gap-3 px-3 py-2 outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring active:bg-surface-low"
            >
              <ProductThumb src={p.primaryImageUrl} alt="" className="size-12 shrink-0 rounded-md" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="type-body min-w-0 truncate font-medium text-on-surface">{p.displayName}</span>
                <span className="type-caption min-w-0 truncate text-on-surface-variant">
                  {[category ? categoryLabel(category) : null, productPriceText(p)].filter(Boolean).join(" · ")}
                </span>
                <span className="flex items-center justify-between gap-2">
                  <StatusPill meta={PRODUCT_SITE_META[productSiteState(p)]} />
                  <span className="type-caption shrink-0">
                    <AvailabilityCell product={p} interactive={false} />
                  </span>
                </span>
              </span>
            </Link>
            <div className="flex shrink-0 items-center pr-2">
              <RowActionsMenu actions={rowActions(p)} size="compact" title={`Действия: ${p.displayName}`} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
